import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Heart, ChevronRight } from 'lucide-react';
import { copy } from '@/copy';

const OnboardingPage = () => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [completing, setCompleting] = useState(false);
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();

  const handleNext = () => {
    if (currentScreen < 3) {
      setCurrentScreen(currentScreen + 1);
    }
  };

  const handleFinish = async () => {
    setCompleting(true);
    try {
      await completeOnboarding();
      navigate('/dashboard');
    } catch {
      navigate('/dashboard');
    }
  };

  const screens = [
    // Screen 1
    <div key="screen1" className="text-center max-w-lg mx-auto animate-fade-in">
      <p className="font-serif text-2xl sm:text-3xl text-[#1D2D44] leading-relaxed mb-6">
        {copy.onboarding.screen1.line1}
      </p>
      <p className="text-lg sm:text-xl text-[#3A4A5A] leading-relaxed">
        {copy.onboarding.screen1.line2}
      </p>
    </div>,

    // Screen 2
    <div key="screen2" className="text-center max-w-lg mx-auto animate-fade-in">
      <p className="text-lg sm:text-xl text-[#1D2D44] leading-relaxed mb-4">
        {copy.onboarding.screen2.line1}
      </p>
      <p className="text-lg sm:text-xl text-[#1D2D44] leading-relaxed mb-8">
        {copy.onboarding.screen2.line2}
      </p>
      <p className="font-serif text-2xl sm:text-3xl text-[#1D2D44] font-medium">
        {copy.onboarding.screen2.line3}
      </p>
    </div>,

    // Screen 3
    <div key="screen3" className="text-center max-w-lg mx-auto animate-fade-in">
      <p className="font-serif text-3xl sm:text-4xl text-[#1D2D44] mb-8">
        {copy.onboarding.screen3.line1}
      </p>
      <p className="text-lg sm:text-xl text-[#3A4A5A] leading-relaxed">
        {copy.onboarding.screen3.line2}
      </p>
    </div>,

    // Screen 4
    <div key="screen4" className="text-center max-w-lg mx-auto animate-fade-in">
      <p className="text-lg sm:text-xl text-[#3A4A5A] leading-relaxed mb-4">
        {copy.onboarding.screen4.line1}
      </p>
      <p className="font-serif text-2xl sm:text-3xl text-[#1D2D44] mb-12">
        {copy.onboarding.screen4.line2}
      </p>
      <Button
        onClick={handleFinish}
        disabled={completing}
        data-testid="onboarding-begin-btn"
        className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-10 py-6 text-lg font-medium shadow-lg transition-all duration-300 hover:scale-[1.02]"
      >
        {completing ? (
          <span className="flex items-center">
            <span className="w-4 h-4 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mr-2"></span>
            Loading...
          </span>
        ) : (
          <>
            {copy.onboarding.screen4.cta}
            <ChevronRight className="ml-2 w-5 h-5" />
          </>
        )}
      </Button>
    </div>,
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      {/* Header */}
      <header className="px-4 py-6">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <span className="font-serif text-lg text-[#1D2D44]">
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-0.5" strokeWidth={1.5} /> Connection
          </span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        {screens[currentScreen]}
      </main>

      {/* Footer with navigation */}
      <footer className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress dots — visual only, not interactive */}
          <div className="flex justify-center gap-2 mb-6">
            {[0, 1, 2, 3].map((index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentScreen
                    ? 'bg-[#1D2D44] w-6'
                    : index < currentScreen
                    ? 'bg-[#5E7F63] w-2'
                    : 'bg-[#1D2D44]/20 w-2'
                }`}
                data-testid={`onboarding-dot-${index}`}
              />
            ))}
          </div>

          {/* Next button (hidden on last screen) */}
          {currentScreen < 3 && (
            <div className="flex justify-center">
              <Button
                onClick={handleNext}
                variant="ghost"
                className="text-[#1D2D44] hover:bg-[#1D2D44]/5 rounded-full px-8 py-3"
                data-testid="onboarding-next-btn"
              >
                Continue
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default OnboardingPage;
